import { expandBox } from './boxMargin';
import { patchCenterToOriginal } from './unletterboxBlob';
import type { UnletterboxContext } from './unletterboxBlob';

export type HeatmapSample = { x: number; y: number; score: number };

export type DeriveHeatmapSamplesArgs = {
  heatmap: Float32Array;
  gridH: number;
  gridW: number;
  ctx: UnletterboxContext;
  // Axis-aligned boxes in the SAME space patchCenterToOriginal returns
  // (cropped-ROI pixels). Only patches whose center falls inside at least one
  // box are emitted, bounding payload to the regions SAM2 actually segments.
  boxes: { x1: number; y1: number; x2: number; y2: number }[];
  // Grow each box outward by this fraction of its size before testing patch
  // membership (see expandBox). The box bounds the above-threshold object, but
  // a cast shadow is sub-threshold and leaks just past that edge — sampling the
  // surrounding ring lets the multimask selector penalize a mask that swallows
  // it. Defaults to 0 (legacy box-clipped behavior).
  marginFraction?: number;
};

// Flatten the DINO patch heatmap into per-patch (x, y, score) samples in
// cropped-image pixel space, restricted to patches inside the given boxes.
// vision-bot scores SAM2's multimask candidates against these samples: a
// candidate is favored when it encloses above-threshold (object) patches and
// avoids below-threshold ones (cast shadow / background), so the
// over-inclusive whole-object mask that swallows a shadow loses. Unlike
// derivePointPrompts (which selects a sparse, capped prompt set), this keeps
// every in-box patch so the selection metric sees dense coverage.
export function deriveHeatmapSamples(
  args: DeriveHeatmapSamplesArgs,
): HeatmapSample[] {
  const { heatmap, gridH, gridW, ctx, boxes, marginFraction = 0 } = args;
  if (boxes.length === 0) return [];

  const expandedBoxes = boxes.map((b) => expandBox(b, marginFraction));

  const samples: HeatmapSample[] = [];

  for (let row = 0; row < gridH; row += 1) {
    for (let col = 0; col < gridW; col += 1) {
      const { x, y } = patchCenterToOriginal(row, col, ctx);

      const inAnyBox = expandedBoxes.some(
        (b) => x >= b.x1 && x <= b.x2 && y >= b.y1 && y <= b.y2,
      );

      if (!inAnyBox) continue;

      samples.push({ x, y, score: heatmap[row * gridW + col] });
    }
  }

  return samples;
}
